import { useEffect, useState, type ReactNode } from 'react'
import { useAuth, SignInButton } from 'headlo-auth'

// Sign-in wall for the mobile app. Blocks the main UI until the user
// completes OAuth via HeadloProviderV2 + Capacitor PlatformAdapter.
//
// Same shape as headlo-desktop's SignInGate — only visual differences are
// mobile-first (larger tap target, safe-area padding, single centered
// action). No SignedInBadge here; that's in Settings on mobile.
//
// Callback-in-flight state: after the OAuth redirect lands, the browser
// URL has ?code= but the client is mid PKCE + DPoP register (takes 1-3s).
// During that window `isLoaded=true, isSignedIn=false` — without special
// casing, the user sees the sign-in button flash and may tap it again.
// Snapshot ?code= presence on first render and stay in a "Signing in…"
// state until isSignedIn flips OR ~10s timeout falls through.

// Read once at module load — subsequent React renders don't matter, we
// only care whether the tab was CREATED with ?code= in the URL.
const HAD_CALLBACK_CODE_AT_BOOT =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('code')

export function SignInGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth()

  // Persist the "we're finishing sign-in" flag until either success or a
  // safety timeout. Cleared automatically when isSignedIn flips true (via
  // the effect below) OR when 10s pass without success (fallback so the
  // user isn't stuck on a permanent spinner if the token exchange fails).
  const [signingIn, setSigningIn] = useState(HAD_CALLBACK_CODE_AT_BOOT)

  useEffect(() => {
    if (!signingIn) return
    if (isSignedIn) { setSigningIn(false); return }
    const id = window.setTimeout(() => setSigningIn(false), 10_000)
    return () => window.clearTimeout(id)
  }, [signingIn, isSignedIn])

  if (!isLoaded || (signingIn && !isSignedIn)) {
    return (
      <div style={s.centered}>
        <div style={s.spinnerWrap}>
          <div style={s.spinnerDot} />
          <div style={s.spinner}>{signingIn ? 'Signing you in…' : 'Loading…'}</div>
        </div>
      </div>
    )
  }

  if (!isSignedIn) {
    return (
      <div style={s.centered}>
        <div style={s.card}>
          <div style={s.logoMark}>H</div>
          <h1 style={s.h1}>Sign in to Headlo</h1>
          <p style={s.lede}>
            Manage orders, view earnings, and get notified about buyer
            activity — from anywhere.
          </p>
          <SignInButton>
            <button style={s.primaryBtn}>Sign in</button>
          </SignInButton>
          <p style={s.footer}>
            No account? Sign-up flow is on the way — for now, create one
            at headlo.com and come back here.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// Uses hex + inline objects to avoid a runtime CSS solution. Mobile stays
// dependency-light — no styled-components / emotion / tailwind. Same
// approach as headlo-desktop.
const s = {
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#faf9f6',
    color: '#1a1a18',
    padding: 'max(24px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom)) 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxSizing: 'border-box' as const,
  },
  spinnerWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 16,
  },
  spinnerDot: {
    width: 32,
    height: 32,
    border: '3px solid #e5e5e0',
    borderTopColor: '#1a1a18',
    borderRadius: '50%',
    animation: 'headloSpin 0.7s linear infinite',
  },
  spinner: {
    fontSize: 14,
    color: '#8a8a80',
  },
  card: {
    width: '100%',
    maxWidth: 360,
    textAlign: 'center' as const,
  },
  logoMark: {
    width: 56,
    height: 56,
    margin: '0 auto 24px',
    borderRadius: 12,
    background: '#2a6a3a',
    color: '#faf9f6',
    fontSize: 24,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  h1: {
    fontSize: 24,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 12,
  },
  lede: {
    fontSize: 15,
    color: '#5a5a55',
    lineHeight: 1.55,
    marginBottom: 32,
  },
  primaryBtn: {
    width: '100%',
    padding: '14px 20px',
    fontSize: 16,
    fontWeight: 600,
    background: '#1a1a18',
    color: '#faf9f6',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  footer: {
    marginTop: 24,
    fontSize: 12,
    color: '#8a8a80',
    lineHeight: 1.5,
  },
} as const
